import React, { memo, useState } from 'react';
import cn from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';

import { Image } from 'components';
import { Button, Form, Input } from 'components/form';
import { Col, Row } from 'components/layout';

import SlideShow from 'components/SlideShow';
import EventCard from 'components/EventCard';
import {
  FlagshipEventAttributes,
  FlagshipEventRequest,
  FlagshipEventResponse,
} from 'lib/models/flagship-event';
import {
  InsightAttributes,
  InsightRequest,
  InsightResponse,
} from 'lib/models/insight';
import { Notice } from 'lib/models/notice';
import { REGEX } from 'lib/utils/regex';

import styles from './Events.module.scss';

export interface EventsProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /**Title */
  title: string;
  /**Label */
  label: string;
  /**List of events */
  events: Array<FlagshipEventAttributes | InsightAttributes>;
  description?: string;
  /**On event add handler */
  onAddEvent: (
    data: FlagshipEventRequest | InsightRequest
  ) => Promise<FlagshipEventResponse | InsightResponse | undefined>;
  /**On event remove handler */
  onRemoveEvent: (id: string) => Promise<Notice | undefined>;
}

export const Events: React.FC<EventsProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    title,
    label,
    events,
    description,
    onAddEvent,
    onRemoveEvent,
  }: EventsProps) => {
    const contentClassNames = cn(
      {
        [styles.Events]: true,
      },
      className
    );
    const [addEvent, setAddEvent] = useState(false);
    const [isLoadingId, setIsLoadingId] = useState('');

    const {
      register,
      handleSubmit,
      watch,
      reset,
      formState: { errors, isSubmitting },
    } = useForm();

    const imageFiles = watch('icon');
    const image =
      imageFiles?.length > 0 ? URL.createObjectURL(imageFiles[0]) : '';

    const onSubmit: SubmitHandler<FlagshipEventRequest | InsightRequest> =
      async (data) => {
        await onAddEvent(data);
        setAddEvent(false);
        reset();
      };

    const onRemove = async (id: string) => {
      setIsLoadingId(id);
      try {
        await onRemoveEvent(id);
        setIsLoadingId('');
      } catch (error) {
        console.error(error);
      }
    };

    const onCancel = () => {
      setAddEvent(false);
      reset();
    };

    return (
      <div className={contentClassNames} style={style} data-testid="Events">
        <header className={styles.EventsHeader}>
          <Row align="center" justify="space-between">
            <Col>
              <h3 className={styles.EventsTitle}>{title}</h3>
            </Col>
            <Col>
              <Button
                color="primary-light"
                disabled={addEvent}
                onClick={() => setAddEvent(true)}
              >
                Add {label}
              </Button>
            </Col>
          </Row>
        </header>
        <p className={styles.EventsDesc}>{description}</p>
        <main>
          <SlideShow
            slidesPerRow={2}
            className={styles.EventsSlideShow}
            negativeMargin
            gutter={32}
          >
            {events.map((event, index) => (
              <div className={styles.EventsSlideShowElement} key={index}>
                <EventCard
                  title={event.title}
                  image={event.icon_url}
                  url={event.url}
                />
                <Button
                  className={styles.EventsSlideShowElementRemove}
                  color="danger"
                  size="small"
                  borderless
                  onClick={() => onRemove(event.id)}
                  isLoading={isLoadingId === event.id}
                >
                  Remove
                </Button>
              </div>
            ))}
          </SlideShow>
          <Row gutter={32}>
            {addEvent && (
              <Col span={12}>
                <Form
                  autoComplete="off"
                  className={styles.EventsNewEvent}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <p className={styles.EventsNewEventTitle}>New {label}</p>
                  {errors.icon?.message ? (
                    <p className={styles.EventsNewEventWarning}>
                      {errors.icon?.message}
                    </p>
                  ) : null}
                  <label htmlFor={`upload-${label}-image`}>
                    <Image
                      src={image ? image : 'upload-image.png'}
                      source={image ? undefined : 'aws'}
                      alt="Upload image"
                      className={styles.EventsNewEventImage}
                    />
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    id={`upload-${label}-image`}
                    {...register('icon', {
                      required: 'Image is required',
                    })}
                    hidden
                  />
                  <Input
                    className={styles.EventsNewEventTitleInput}
                    placeholder="Enter the title"
                    {...register('title', {
                      required: 'Title is required',
                    })}
                    isValid={!errors.title}
                    helperText={errors.title?.message}
                  />
                  <Input
                    className={styles.EventsNewEventUrl}
                    type="url"
                    placeholder="Enter URL"
                    {...register('url', {
                      required: 'URL is required',
                      pattern: {
                        value: REGEX.URL,
                        message: 'Enter a valid url',
                      },
                    })}
                    isValid={!errors.url}
                    helperText={errors.url?.message}
                  />
                  <div className={styles.EventsNewEventActions}>
                    <Button
                      size="medium"
                      color="danger"
                      type="button"
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="medium"
                      type="submit"
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                    >
                      Add
                    </Button>
                  </div>
                </Form>
              </Col>
            )}
          </Row>
        </main>
      </div>
    );
  }
);

Events.displayName = 'Events';
